import React, { useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { I18nProvider, useI18n } from './i18n/i18nContext';
import Layout from './components/Layout';
import LanguageRouter from './components/LanguageRouter';
import ScrollToTop from './components/ScrollToTop';
import { getUrlPrefix } from './utils/languageUtils';
import AuthTokenManager from './components/AuthTokenManager';
import { UserProvider } from './contexts/UserContext';
import { PromotionProvider } from './contexts/PromotionContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { useServiceMetaStore } from './stores/serviceMetaStore';

// Custom AppState interface with our properties
interface CustomAppState {
  returnTo?: string;
  from?: string;
  state?: any; // For preserving location state during auth redirect
}

function AppContent() {
  // Note: Each page sets its own title via useDocumentTitle() hook
  // The default title is set in public/index.html as fallback
  const fetchServiceCredits = useServiceMetaStore(state => state.fetchServiceCredits);

  useEffect(() => {
    fetchServiceCredits();
  }, [fetchServiceCredits]);
  
  return (
    <ImpersonationProvider>
      <PromotionProvider>
        <UserProvider>
          <AuthTokenManager />
          <ScrollToTop />
          <Layout>
            <LanguageRouter />
          </Layout>
        </UserProvider>
      </PromotionProvider>
    </ImpersonationProvider>
  );
}

const AuthProviderWithRedirect: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { language, t } = useI18n();

  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const redirectUri = `${window.location.origin}/callback`;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  // Validate required configuration
  if (!domain || !clientId) {
    console.error(t.errors.auth0ConfigMissing);
    return <div>{t.errors.auth0ConfigError}</div>;
  }

  const onRedirectCallback = (appState?: CustomAppState) => {
    
    // Note: Registration completion will be checked by ProtectedRoute
    // We don't need to check localStorage here anymore
    const returnTo = appState?.returnTo || '/';
    const fromHeader = appState?.from === 'header';
    const langPrefix = '/' + getUrlPrefix(language);




    // If login was initiated from the header, always redirect to account page
    if (fromHeader) {
      navigate(`${langPrefix}/account`, { replace: true });
    } else {
      // Restore state if it was preserved during redirect
      // Note: Registration completion check is now handled by ProtectedRoute
      navigate(returnTo, { replace: true, state: appState?.state });
    }
  };

  // Build authorization params
  const authorizationParams: any = {
    redirect_uri: redirectUri,
    scope: 'openid profile email offline_access',
    audience: audience || 'https://dev-fire-and-ice.eu.auth0.com/api/v2/' // Auth0 Management API
  };

  const skipRedirectCallback = window.location.pathname !== '/callback';

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      skipRedirectCallback={skipRedirectCallback}
      useRefreshTokens={true}
    >
      {children}
    </Auth0Provider>
  );
};

function App() {
  return (
    <Router>
      <I18nProvider>
        <AuthProviderWithRedirect>
          <AppContent />
        </AuthProviderWithRedirect>
      </I18nProvider>
    </Router>
  );
}

export default App;
